import { ChangeDetectionStrategy, Component, DestroyRef, type InputSignal, type OnInit, type Signal, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, type AbstractControl, type FormControl } from '@angular/forms';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { LocationState } from '../../states/location.state';
import type { IPhoneInfoFormValue } from '../../models/phone-info-form-value.interface';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FieldLabelComponent } from '@pecan/shared/components/field-label/field-label.component';
import { InvalidLabelComponent } from '@pecan/shared/components/invalid-label/invalid-label.component';
import { PHONE_CODES } from '@pecan/shared/constants/phone-masks.constant';
import { PHONE_CODE_LENGTH } from '@pecan/shared/constants/phone-code-length.constant';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, FieldLabelComponent, InvalidLabelComponent, MatFormField, MatError, MatSelect, MatOption, TranslatePipe],
    selector: 'app-time-zone',
    templateUrl: './time-zone.component.html',
})
export class TimeZoneComponent implements OnInit {
    public readonly label: InputSignal<string> = input<string>('time_zone');

    public readonly phoneInfoCtrl: InputSignal<AbstractControl<IPhoneInfoFormValue>> =
        input.required<AbstractControl<IPhoneInfoFormValue>>();

    public readonly countryCode: InputSignal<string> = input.required<string>();

    public readonly timeZoneCtrl: InputSignal<FormControl<string>> = input.required<FormControl<string>>();

    public readonly areaCodes: Signal<Record<string, string> | null>;

    public readonly timeZoneKeyValueList: Signal<Record<string, string>[] | null>;

    private readonly _destroyRef: DestroyRef = inject(DestroyRef);

    private _store: Store = inject(Store);

    constructor() {
        this.areaCodes = this._store.selectSignal(LocationState.areaCodes);
        this.timeZoneKeyValueList = this._store.selectSignal(LocationState.timeZoneKeyValueList);
    }

    public ngOnInit(): void {
        this._setupPhoneControl();
    }

    private _getPhoneCode(value: string): string {
        return value?.slice(0, PHONE_CODE_LENGTH);
    }

    private _setUpTimeZone(): void {
        const areaCodes = this.areaCodes();

        if (!areaCodes) {
            return;
        }

        const phoneInfo = this.phoneInfoCtrl().value;
        const currentCountryCode = phoneInfo?.locale?.countryCode ?? this.countryCode();
        const phone = phoneInfo?.phone;
        const isUSCanada = currentCountryCode === PHONE_CODES.US_CANADA;
        const hasPhoneValue = Boolean(phone?.length);

        if (isUSCanada && hasPhoneValue) {
            const areaCodeValue = areaCodes[this._getPhoneCode(phone)];
            this.timeZoneCtrl().setValue(areaCodeValue ?? '', { emitEvent: false });
        }
    }

    private _setupPhoneControl(): void {
        this.phoneInfoCtrl()
            .valueChanges.pipe(
                map((v: IPhoneInfoFormValue) => v?.phone),
                distinctUntilChanged((prev: string, curr: string) => this._getPhoneCode(prev) === this._getPhoneCode(curr)),
                takeUntilDestroyed(this._destroyRef),
                filter(Boolean)
            )
            .subscribe(() => this._setUpTimeZone());
    }
}
